import { useApolloClient } from '@apollo/client';
import { SaveCancelWidget } from 'components';
import { WebServiceActionTypes, WebServiceContext } from 'contexts/WebServiceContext';
import { ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import type { ActionButtonProps } from 'contracts/interfaces/ActionButtonProps';
import { isEqual } from 'lodash';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { GetWebServiceDocument, useUpdateWebServiceMutation } from 'services/graphql/generated/hooks';
import { getParams } from 'utils';

export const WebServiceActionButtons: FC<ActionButtonProps> = ({ isEditable }) => {
  const client = useApolloClient();
  const ctx = useContext(WebServiceContext);
  const { pathname } = useLocation();
  const params = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setIsSaved(false);
  }, [ctx?.state]);

  const webServiceData = client.readQuery({
    query: GetWebServiceDocument,
    variables: {
      uuid: params.entityId,
      packageVersionUuid: params.versionUuid
    }
  });

  /* TODO: Handle the error from this operation. Ticket for the implementation:
     https://deitycommerce.atlassian.net/browse/DI-564 */
  const [updateWebService, { loading: isSaving, error: saveError }] = useUpdateWebServiceMutation({
    refetchQueries: [
      {
        query: GetWebServiceDocument,
        variables: {
          packageVersionUuid: ctx?.state.packageVersionUuid,
          uuid: ctx?.state.uuid
        }
      }
    ]
  });
  if (saveError) {
    // eslint-disable-next-line no-console
    console.error('Error updating webServiceSchema ', saveError);
  }

  const saveWebServiceConfig = () => {
    if (params?.entityId && ctx?.state) {
      // remove package version uuid from schema
      const schema = { ...ctx.state.schema };

      updateWebService({
        variables: {
          packageVersionUuid: params.versionUuid,
          uuid: params.entityId,
          schema,
          name: ctx.state.name,
          description: ctx.state.description
        },
        notifyOnNetworkStatusChange: true
      }).then(() => setIsSaved(true));
    }
  };

  const cancelChange = () => {
    if (webServiceData?.getWebService.schema) {
      ctx?.dispatch({
        type: WebServiceActionTypes.INIT_WEB_SERVICE_CONFIG,
        payload: {
          name: webServiceData.getWebService.name,
          description: webServiceData.getWebService.description,
          schema: webServiceData.getWebService.schema,
          uuid: webServiceData.getWebService.uuid,
          packageVersionUuid: webServiceData.getWebService.packageVersionUuid
        }
      });
    }
  };

  const renderCondition =
    !isSaved &&
    webServiceData?.getWebService.schema &&
    (!isEqual(webServiceData?.getWebService.schema, ctx?.state.schema) ||
      !isEqual(webServiceData?.getWebService.description, ctx?.state.description || '') ||
      !isEqual(webServiceData?.getWebService.name, ctx?.state.name || ''));

  return (isSaving || renderCondition) && isEditable ? (
    <SaveCancelWidget onCancel={cancelChange} onSave={saveWebServiceConfig} isSaving={isSaving} />
  ) : null;
};
